import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Container,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Stack,
  Box,
} from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ContentBodyBox from './ContentBodyBox';
import CounterWithLabel from './CaptionValue/CounterWithLabel';
import ChangeBodyValueDialog from '../Pages/CustomerPage/Shared/ChangeBodyValueDialog';
import StyledTooltip from './StyledTooltip';

const useStyles = () => ({
  icnWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  smsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '60%',
  },
  charCounter: {
    position: 'absolute',
    bottom: '10px',
    right: '6px',
  },
});

const totalCustomersInvolved = (sourceArray) =>
  sourceArray.reduce((acc, curr) => acc + (curr?.involvedCustomers || 0), 0);

//this component needs to be wrapped for api calls and values
//the wrapper is a widget for TemplateMessageDialog
const SmsWidget = function SmsWidget({
  selectedTemplate,
  handleChangeTemplate,
  templates = [],
  customersInvolvedCounts = [],
  channel = null,
  customMessage = false,
  massive = false,
  checkSubmit = () => {},
  setContentSms = () => {},
  setContentEmail = () => {},
  failedChecks = [],
}) {
  const intl = useIntl();
  const classes = useStyles();

  const [inputValue, setInputValue] = useState('');
  const [tab, setTab] = useState('SMS');
  const [dynamicElements, setDynamicElements] = useState([]);
  const [changeValueDialog, setChangeValueDialog] = useState(null);

  const counter =
    customersInvolvedCounts.length > 0
      ? totalCustomersInvolved(customersInvolvedCounts)
      : massive
        ? 0
        : 1;

  useEffect(() => {
    if (selectedTemplate === 99) {
      setInputValue('');
      setContentSms('');
      setContentEmail('');
    } else if (selectedTemplate !== null) {
      const template = templates[selectedTemplate];
      if (channel === 'BOTH') {
        const messageValue =
          tab === 'SMS'
            ? template?.parsedSMS || template?.contentSMS
            : template?.parsedEmail || template?.contentEmail;
        setDynamicElements(messageValue?.match(/§(.*?)§/g));
        setInputValue(messageValue);
      } else {
        const messageValue =
          channel === 'SMS'
            ? template?.parsedSMS || template?.contentSMS
            : template?.parsedEmail || template?.contentEmail;
        setDynamicElements(messageValue?.match(/§(.*?)§/g));
        setInputValue(messageValue);
      }
    }
  }, [channel, tab, selectedTemplate]);

  useEffect(() => {
    const check =
      selectedTemplate !== null &&
      (channel === 'EMAIL' ||
        ((!failedChecks || failedChecks.length === 0) && channel !== 'EMAIL'));

    if (inputValue) {
      checkSubmit(
        check &&
          (dynamicElements
            ? dynamicElements?.every((el) => !inputValue.includes(el))
            : true)
      );
    } else {
      checkSubmit(check);
    }
  }, [inputValue, dynamicElements, channel, failedChecks, selectedTemplate]);

  const onChangeInputValue = (event) => {
    setInputValue(event.target.value);
    if (channel === 'SMS' || channel === 'BOTH')
      setContentSms(event.target.value);
    if (channel === 'EMAIL' || channel === 'BOTH')
      setContentEmail(event.target.value);
  };
  const handleChangeTab = (event, newValue) => {
    setTab(newValue);
  };

  const openChangeValueDialog = (el) => {
    setChangeValueDialog(el);
  };
  const closeChangeValueDialog = () => {
    setChangeValueDialog(null);
  };

  useEffect(() => {
    window.changValueDialog = openChangeValueDialog;
    return () => {
      delete window.changValueDialog;
    };
  });

  //replaces the message value string replacing the dynamic element with the inserted value in changeValueDialog
  const handleSubmit = (oldValue, newValue) => {
    setInputValue((current) => current?.replace(oldValue, `§${newValue}§`));
    setContentSms((current) => current?.replace(oldValue, `§${newValue}§`));
    setContentEmail((current) => current?.replace(oldValue, `§${newValue}§`));
    closeChangeValueDialog();
  };

  return (
    <>
      <ChangeBodyValueDialog
        open={changeValueDialog}
        onClose={closeChangeValueDialog}
        onSubmit={handleSubmit}
      />
      <div style={{ display: 'flex' }}>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '40%',
            gap: '16px',
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{
              mb: 2
            }}>
              {intl.messages.SELECT_PREDEFINED_MESSAGE}
            </Typography>
            <ToggleButtonGroup
              color="primary"
              fullWidth
              orientation="vertical"
              value={selectedTemplate}
              exclusive
              onChange={handleChangeTemplate}
            >
              {templates?.map((t, index) => {
                if (
                  (channel === 'BOTH' && t?.contentSMS && t?.contentEmail) ||
                  (channel === 'SMS' && t?.contentSMS) ||
                  (channel === 'EMAIL' && t?.contentEmail)
                )
                  return (
                    <ToggleButton key={t.name} value={index}>
                      {t.name}
                    </ToggleButton>
                  );
                return null;
              })}
              {customMessage && (
                <ToggleButton id="custom-template-btn" key={99} value={99}>
                  {intl.messages.CUSTOM}
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </div>
        </Container>
        <Container sx={classes.smsSection}>
          <Stack direction="row" sx={{
            justifyContent: "space-between"
          }}>
            <CounterWithLabel
              reverse
              color="success.main"
              label={intl.messages.CHARS}
              value={inputValue?.length || 0}
            />
            <CounterWithLabel
              reverse
              label={
                channel === 'SMS'
                  ? intl.messages.SMS_COUNTER
                  : intl.messages.EMAIL_COUNTER
              }
              value={
                channel === 'SMS'
                  ? (inputValue && Math.ceil(inputValue.length / 160)) * counter
                    ? (inputValue && Math.ceil(inputValue.length / 160)) *
                      counter
                    : 0
                  : counter
              }
            />
            <CounterWithLabel
              reverse
              color="graphs.purple"
              label={intl.messages.CUSTOMERS_INVOLVED}
              value={counter}
              title={
                customersInvolvedCounts?.length > 0 && (
                  <Box sx={{
                    p: "4px"
                  }}>
                    <p style={{ fontSize: '1rem' }}>
                      {intl.messages.CUSTOMERS_INVOLVED_PER_SECTION}:
                    </p>

                    {customersInvolvedCounts?.map((el) => (
                      <p key={el?.branchId} style={{ fontSize: '1rem' }}>
                        {`${el?.branchDescription}: ${el?.involvedCustomers}`}
                      </p>
                    ))}
                  </Box>
                )
              }
            />
          </Stack>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "8px"
            }}>
            {selectedTemplate !== 99 ? (
              <ContentBodyBox
                tabValue={tab}
                onChangeTabValue={handleChangeTab}
                tabs={channel === 'BOTH' ? ['EMAIL', 'SMS'] : []}
                content={inputValue}
                removeHtml
                height="28vh"
                styles={{
                  content: {
                    fontFamily: 'Roboto',
                  },
                }}
              />
            ) : (
              <TextField
                id="sms-text-area"
                multiline
                rows={10}
                value={inputValue}
                placeholder={intl.messages.WRITE_MESSAGE}
                onChange={onChangeInputValue}
                fullWidth
              />
            )}
            {failedChecks?.length > 0 && channel !== 'EMAIL' ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center"
                }}>
                <Typography variant="body1" sx={{ color: 'greys.darker' }}>
                  {intl.messages.SMS_SEND_BLOCKED}
                </Typography>
                <StyledTooltip
                  title={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px"
                      }}>
                      <Typography variant="h6">
                        {intl.messages.REASONS}
                      </Typography>
                      {failedChecks.map((el) => (
                        <Typography key={el} variant="body1" color="error">
                          {intl.messages[el] ?? el}
                        </Typography>
                      ))}
                    </Box>
                  }
                  placement="right"
                >
                  <HelpOutlineRoundedIcon sx={{ color: 'greys.darker' }} />
                </StyledTooltip>
              </Box>
            ) : (
              <div />
            )}
          </Box>
        </Container>
      </div>
    </>
  );
};

SmsWidget.propTypes = {
  templates: PropTypes.array,
  selectedTemplate: PropTypes.number.isRequired,
  handleChangeTemplate: PropTypes.func.isRequired,
  customersInvolvedCounts: PropTypes.array,
  channel: PropTypes.string,
  customMessage: PropTypes.bool,
  massive: PropTypes.bool,
  checkSubmit: PropTypes.func,
  setContentSms: PropTypes.func,
  setContentEmail: PropTypes.func,
  failedChecks: PropTypes.array,
};

export default SmsWidget;
